import React from "react";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { formatInstantDateShortTr } from "../../../utils/appTime";
import { computeSaleMoneyTotals } from "../order/orderDetailUtils";
import ProductPrice from "../../../components/CustomUI/ProductPrice";
import { useExchangeRateOptional } from "../../../contexts/exchangeRate";
import "../order/orderDetail.css";

const PAYMENT_LABELS = {
  cash: "Nakit",
  credit_card: "Kredi Kartı",
  bank_transfer: "Havale / EFT",
  document: "Çek / Senet",
};

const PAYMENT_STATUS_LABELS = {
  pending: "Bekliyor",
  paid: "Ödendi",
  partial: "Kısmi",
  refunded: "İade",
};

const SALE_STATUS_LABELS = {
  pending: "Bekliyor",
  completed: "Tamamlandı",
  cancelled: "İptal",
  partial_delivery: "Kısmi Sevkiyat",
};

const APPROVAL_LABELS = {
  pending: { label: "Onay Bekliyor", appearance: "warning-subtle" },
  approved: { label: "Onaylandı", appearance: "success-subtle" },
  rejected: { label: "Reddedildi", appearance: "danger-subtle" },
  not_required: { label: "Onay Gerekmiyor", appearance: "default" },
};

const val = (v) => (v === 0 || v ? v : "-");

const SaleDetailBody = ({ sale, format, rejectReason, detailSection = "all" }) => {
  const fx = useExchangeRateOptional();
  if (!sale) {
    return <p className="text-muted mb-0">Sipariş detayı yüklenemedi.</p>;
  }

  const showInfo = detailSection === "all" || detailSection === "info";
  const showModels = detailSection === "all" || detailSection === "models";
  const showDescriptions =
    detailSection === "all" || detailSection === "descriptions";

  const approval = APPROVAL_LABELS[sale.approvalStatus] || {
    label: sale.approvalStatus || "-",
    appearance: "default",
  };
  const orderRef =
    typeof sale.orderId === "object"
      ? sale.orderId?.orderName || sale.orderId?._id
      : sale.orderId;
  const moneyTotals = computeSaleMoneyTotals(sale);
  const notes = sale.notes;

  return (
    <div className="order-detail">
      {showInfo ? (
      <div className="detail-section mb-0">
        <strong>Sipariş Bilgileri</strong>
        <div className="order-detail-grid mt-2">
          <div className="detail-row">
            <span className="detail-label">Sipariş Adı</span>
            <span>{val(sale.saleName)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Firma</span>
            <span>
              {sale.companyId?.name || "-"}
              {sale.companyId?.code ? ` (${sale.companyId.code})` : ""}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Kaynak Teklif</span>
            <span>{val(orderRef)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Onay Durumu</span>
            <Lozenge appearance={approval.appearance}>{approval.label}</Lozenge>
          </div>
          <div className="detail-row">
            <span className="detail-label">Sipariş Durumu</span>
            <span>
              {SALE_STATUS_LABELS[sale.status] || sale.status || "-"}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Ödeme Yöntemi</span>
            <span>
              {PAYMENT_LABELS[sale.paymentMethod] || sale.paymentMethod || "-"}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Ödeme Durumu</span>
            <span>
              {PAYMENT_STATUS_LABELS[sale.paymentStatus] ||
                sale.paymentStatus ||
                "-"}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Sipariş Tarihi</span>
            <span>
              {sale.saleDate ? formatInstantDateShortTr(sale.saleDate) : "-"}
              {sale.saleTime ? ` · ${sale.saleTime}` : ""}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Döviz Kuru</span>
            <span>
              {sale.exchangeRate
                ? `1 USD = ${Number(sale.exchangeRate).toLocaleString("tr-TR", {
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                  })} TL`
                : fx?.rate
                  ? `1 USD = ${Number(fx.rate).toLocaleString("tr-TR", {
                      minimumFractionDigits: 4,
                      maximumFractionDigits: 4,
                    })} TL *`
                  : "-"}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Toplam Cihaz</span>
            <span>{val(sale.totalDevices)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Son Sevkiyat</span>
            <span>
              {sale.lastDeliveryDate
                ? formatInstantDateShortTr(sale.lastDeliveryDate)
                : "-"}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Oluşturan</span>
            <span>{val(sale.createdBy?.name)}</span>
          </div>
          {rejectReason && (
            <div className="detail-row">
              <span className="detail-label">Red Nedeni</span>
              <span className="text-muted">{rejectReason}</span>
            </div>
          )}
        </div>
      </div>
      ) : null}

      {showModels ? (
        <>
      <div className="detail-section mb-0">
        <strong>Modeller</strong>
        <div className="mt-2">
          <Table
            columns={[
              { id: 0, name: "Kod", dataType: "text" },
              { id: 1, name: "Model", dataType: "text" },
              { id: 2, name: "Adet", dataType: "number" },
              { id: 3, name: "Sevk", dataType: "number" },
              { id: 4, name: "Birim Fiyat", dataType: "number" },
              { id: 5, name: "Satır tutarı", dataType: "number" },
            ]}
          >
            {(sale.models || []).map((m, i) => {
              const price = m.modelId?.price || 0;
              const count = m.count || 0;
              return (
                <Tr key={i} id={i} data={m}>
                  <Td>
                    <span className="fw-semibold">
                      {m.modelId?.code || "-"}
                    </span>
                  </Td>
                  <Td>{m.modelId?.name || "-"}</Td>
                  <Td>{count}</Td>
                  <Td>{m.deliveredCount ?? 0}</Td>
                  <Td>
                    <span className="text-muted">
                      <ProductPrice value={price} rate={sale.exchangeRate} />
                    </span>
                  </Td>
                  <Td>
                    <span className="text-primary fw-semibold">
                      <ProductPrice
                        value={price * count}
                        rate={sale.exchangeRate}
                      />
                    </span>
                  </Td>
                </Tr>
              );
            })}
          </Table>
        </div>
      </div>

      <div className="detail-totals">
        <div className="detail-totals-row">
          <span>Ara toplam</span>
          <span>
            <ProductPrice
              value={moneyTotals.netTotal}
              rate={sale.exchangeRate}
            />
          </span>
        </div>
        <div className="detail-totals-row">
          <span>KDV (%20)</span>
          <span>
            <ProductPrice value={moneyTotals.kdv} rate={sale.exchangeRate} />
          </span>
        </div>
        <div className="detail-totals-row detail-totals-grand">
          <span>Genel Toplam (KDV dahil)</span>
          <span className="text-primary">
            <ProductPrice
              value={moneyTotals.grandTotal}
              rate={sale.exchangeRate}
            />
          </span>
        </div>
      </div>
        </>
      ) : null}

      {showDescriptions && notes ? (
        <div className="detail-section mb-0">
          <strong>Açıklamalar</strong>
          <div className="order-detail-grid mt-2">
            <div className="detail-row detail-row-full">
              <span className="detail-label">Notlar</span>
              <span className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                {notes}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SaleDetailBody;
